import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useState } from 'react';
import { ROUTE_PATHS, ARTICLE_SECTIONS } from '@/lib/index';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4">
          <div className="flex h-16 items-center justify-between">
            <Link to={ROUTE_PATHS.HOME} className="flex items-center space-x-2">
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-accent">
                <span className="text-xl font-bold text-primary-foreground">AI</span>
              </div>
              <span className="text-xl font-semibold text-foreground">Headshot Guide</span>
            </Link>

            <nav className="hidden md:flex items-center space-x-6">
              <a href="#introduction" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                Introduction
              </a>
              <a href="#top-generators" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                Reviews
              </a>
              <a href="#key-features" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                Features
              </a>
              <a href="#pricing" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                Pricing
              </a>
            </nav>

            <button
              className="md:hidden p-2 text-muted-foreground hover:text-foreground"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {mobileMenuOpen && (
            <nav className="md:hidden py-4 border-t border-border">
              <div className="flex flex-col space-y-3">
                {ARTICLE_SECTIONS.slice(0, 4).map((section) => (
                  <a
                    key={section.id}
                    href={`#${section.anchor}`}
                    className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {section.title}
                  </a>
                ))}
              </div>
            </nav>
          )}
        </div>
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-border bg-muted/30">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-accent">
                  <span className="text-sm font-bold text-primary-foreground">AI</span>
                </div>
                <span className="text-lg font-semibold text-foreground">Headshot Guide</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Your comprehensive resource for AI-powered professional headshot generators in 2026.
              </p>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-foreground mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li>
                  <a href="#introduction" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    Introduction
                  </a>
                </li>
                <li>
                  <a href="#top-generators" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    Top Generators
                  </a>
                </li>
                <li>
                  <a href="#key-features" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    Key Features
                  </a>
                </li>
                <li>
                  <a href="#pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    Pricing Guide
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-foreground mb-4">Resources</h3>
              <ul className="space-y-2">
                <li>
                  <a href="#how-they-work" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    How It Works
                  </a>
                </li>
                <li>
                  <a href="#use-cases" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    Use Cases
                  </a>
                </li>
                <li>
                  <a href="#quality-factors" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    Quality Factors
                  </a>
                </li>
                <li>
                  <a href="#conclusion" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                    Recommendations
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-border">
            <p className="text-sm text-muted-foreground text-center">
              © 2026 AI Headshot Guide. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
